import React from 'react';

import refreshListings from 'app-core/platform/listable/refreshListings';
import {
  StarrableItemsQuery,
  StarrableItemsQueryVariables,
} from 'app-core/platform/starrable/__generated/StarrableItemsQuery';
import {STARRED_ITEMS_QUERY} from 'app-core/platform/starrable/CoreObjectStarIcon';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PROJECT_TYPE, SidepanelItemSource, SourceSummary} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
// @ts-expect-error
import FileMenu from 'file-browser/FileMenu';
import FinderUtil from 'file-browser/FinderUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {hasAnyOrgsWithDisablePersonalOwnables} from 'orgs/DisablePersonalOwnablesUtil';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {setDefaultRegistry} from 'registry/DefaultRegistry';
import * as RegistryUtil from 'registry/RegistryUtil';
import EntityStore from 'stores/EntityStore';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {ElementMouseEvent} from 'util/Types';

import DragAndDropSidepanelItemWrapper from './DragAndDropSidepanelItemWrapper';
import {ProjectsListingProject} from './ProjectsListing';
import SidepanelItem from './SidepanelItem';

export interface SourceSidepanelItemProps {
  finderId: string;
  openResult(result: StringIDFolder): void;
  showCreator: boolean;
  showOwner: boolean;
  showStar: boolean;
  source: SidepanelItemSource;
  isChild: boolean;
}

interface State {
  isMenuOpen: boolean;
}

function isProject(source: SourceSummary): source is ProjectsListingProject {
  return source.sourceType === PROJECT_TYPE;
}

export default class SourceSidepanelItem extends React.PureComponent<SourceSidepanelItemProps> {
  _isMounted = false;

  state: State = {
    isMenuOpen: false,
  };

  componentDidMount(): void {
    this._isMounted = true;
  }

  componentWillUnmount(): void {
    this._isMounted = false;
  }

  private handleOpen = (): void => {
    if (RegistryUtil.isRegistry(this.props.source)) {
      setDefaultRegistry(this.props.source.id);
    }
    const result = FinderUtil.sourceToRootFolder(this.props.source);
    this.props.openResult(result);
  };

  private handleContextMenuClose = (): void => {
    if (this._isMounted) {
      this.setState({isMenuOpen: false});
    }
  };

  private handleShowMenuOptions = (e: ElementMouseEvent): void => {
    FileMenu.open(e, {
      file: this.props.source,
      finderId: this.props.finderId,
      isListing: true,
      closeCallback: this.handleContextMenuClose,
    });
    this.setState({isMenuOpen: true});
  };

  private handleClickStar = async (): Promise<void> => {
    if (isProject(this.props.source)) {
      await FileBrowserActions.toggleProjectStar(this.props.source);
      await doQuerySafe<StarrableItemsQuery, StarrableItemsQueryVariables>(
        {query: STARRED_ITEMS_QUERY},
        {variables: {ids: [this.props.source.rootFolder.api_identifier]}, fetchPolicy: 'network-only'}
      );
      refreshListings();
    }
  };

  renderTitle(): JSX.Element {
    const {source, showOwner} = this.props;

    const currentUser = EntityStore.currentUser();
    const isCurrentUserOwner = currentUser && currentUser.handle === source.s_owner.handle;
    if (RegistryUtil.isRegistry(source)) {
      return <span>{source.s_owner.name}</span>;
    }
    // Handle a bunch of ways to find owner.
    const sourceOwnerDisplayString =
      (source.s_owner && getPartyDisplayString(source.s_owner)) ||
      (source.creator && getPartyDisplayString(source.creator)) ||
      source.creator_handle;
    const shouldShowOwner =
      !hasAnyOrgsWithDisablePersonalOwnables() &&
      showOwner &&
      !isCurrentUserOwner &&
      sourceOwnerDisplayString;
    return (
      <span>
        {shouldShowOwner && (
          <span className="faded">
            {sourceOwnerDisplayString}
            {' / '}
          </span>
        )}
        {source.name}
      </span>
    );
  }

  render(): JSX.Element {
    const {source} = this.props;
    let collaborators = source.collaborators || [];
    if (isProject(source)) {
      collaborators = collaborators.filter((collaborator) => collaborator.type === ModelTypeConstants.TEAM);
    }
    return (
      <DragAndDropSidepanelItemWrapper
        canInteract={this.state.isMenuOpen}
        finderId={this.props.finderId}
        result={this.props.source}
        shouldCaptureDrop={isProject(source)}
      >
        <SidepanelItem
          active={false}
          className="sidepanelItem--dnd"
          faded={isProject(source) && (source.archived || source.hidden || Boolean(source.archiveRecord))}
          handleClickStar={this.handleClickStar}
          icon={isProject(source) ? <BenchlingIcon icon={ModelTypeConstants.PROJECT} /> : null}
          isCompact={false}
          onClick={this.handleOpen}
          onContextMenu={this.handleShowMenuOptions}
          parties={collaborators}
          showStar={this.props.showStar}
          starred={source.starred}
          subTitle={this.props.showCreator && source.creator ? source.creator.name : ''}
          title={{text: this.props.source.name, node: this.renderTitle()}}
          isExpandable={false}
          isChild={this.props.isChild}
        />
      </DragAndDropSidepanelItemWrapper>
    );
  }
}
